import React, { useEffect, useState } from 'react';
import { Video, Check, X, Flag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-600 border-rose-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
};

/** Admin/staff view of all video-call bookings with confirm / cancel / complete. */
export default function AppointmentsAdmin() {
  const [appointments, setAppointments] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/admin/appointments`, { credentials: 'include' });
      if (res.ok) setAppointments((await res.json()).appointments || []);
    } catch { /* offline */ }
  };

  useEffect(() => { load(); }, []);

  const update = async (a, status) => {
    let meeting_link;
    if (status === 'confirmed') {
      meeting_link = window.prompt('Paste the video meeting link (Zoom / Meet) to include in the confirmation:', a.meeting_link || '') || '';
    }
    setBusyId(a.appointment_id);
    try {
      const res = await fetch(`${API}/api/admin/appointments/${a.appointment_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, ...(meeting_link ? { meeting_link } : {}) }),
      });
      if (res.ok) await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden" data-testid="appointments-admin">
      {appointments.length === 0 ? (
        <div className="text-center py-12 text-sm text-tge-muted">No video-call bookings yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-tge-cream-soft text-tge-muted text-[10px] uppercase tracking-wide">
            <tr>
              {['When', 'Student', 'Booked by', 'Topic', 'Status', 'Actions'].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-tge-cream">
            {appointments.map((a) => (
              <tr key={a.appointment_id} data-testid={`admin-appointment-${a.appointment_id}`}>
                <td className="px-4 py-3 whitespace-nowrap font-medium text-tge-navy">
                  <span className="inline-flex items-center gap-1.5"><Video size={12} className="text-indigo-500" /> {a.date} · {a.time}</span>
                </td>
                <td className="px-4 py-3">{a.student_name || a.student_id}</td>
                <td className="px-4 py-3 text-xs">
                  {a.booked_by?.name}
                  <span className="text-tge-muted"> · {a.booked_by?.role}</span>
                </td>
                <td className="px-4 py-3 text-xs text-tge-muted max-w-[220px] truncate">{a.topic || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-semibold uppercase border rounded-full px-2 py-0.5 ${STATUS_STYLE[a.status] || ''}`}>{a.status}</span>
                  {a.meeting_link && <a href={a.meeting_link} target="_blank" rel="noreferrer" className="block text-[10px] text-indigo-600 underline mt-0.5 truncate max-w-[140px]">{a.meeting_link}</a>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {a.status === 'pending' && (
                      <button onClick={() => update(a, 'confirmed')} disabled={busyId === a.appointment_id}
                        className="text-[11px] font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`appointment-confirm-${a.appointment_id}`}>
                        <Check size={11} /> Confirm
                      </button>
                    )}
                    {a.status !== 'cancelled' && a.status !== 'completed' && (
                      <button onClick={() => update(a, 'cancelled')} disabled={busyId === a.appointment_id}
                        className="text-[11px] font-semibold text-rose-600 border border-rose-200 bg-rose-50 hover:bg-rose-100 rounded-lg px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`appointment-cancel-${a.appointment_id}`}>
                        <X size={11} /> Cancel
                      </button>
                    )}
                    {a.status === 'confirmed' && (
                      <button onClick={() => update(a, 'completed')} disabled={busyId === a.appointment_id}
                        className="text-[11px] font-semibold text-slate-600 border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-lg px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`appointment-complete-${a.appointment_id}`}>
                        <Flag size={11} /> Done
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
